import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

interface AuthFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  index: string;
  error?: string;
  testId: string;
}

export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(
  ({ label, index, error, testId, type, className, onFocus, onBlur, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const [revealed, setRevealed] = useState(false);
    const isPassword = type === "password";
    const inputType = isPassword && revealed ? "text" : type;

    return (
      <div className="flex flex-col gap-1.5">
        <div
          className={cn(
            "group relative rounded-2xl border bg-white/[0.03] backdrop-blur-xl transition-[border-color,box-shadow] duration-300",
            error
              ? "border-red-400/60"
              : focused
                ? "border-safety/70 shadow-[0_0_32px_-12px_rgba(255,91,0,0.55)]"
                : "border-cad/30 hover:border-cad/60",
          )}
        >
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-safety to-transparent"
            initial={false}
            animate={{ opacity: focused ? 1 : 0, scaleX: focused ? 1 : 0.4 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          />
          <div className="flex items-center justify-between px-4 pt-3">
            <label
              htmlFor={testId}
              className={cn(
                "font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-300",
                focused ? "text-safety" : "text-cad",
              )}
            >
              {label}
            </label>
            <span className="font-mono text-[10px] tracking-[0.18em] text-cad/60">{index}</span>
          </div>
          <div className="flex items-center">
            <input
              ref={ref}
              id={testId}
              data-testid={testId}
              type={inputType}
              onFocus={(e) => { setFocused(true); onFocus?.(e); }}
              onBlur={(e) => { setFocused(false); onBlur?.(e); }}
              className={cn(
                "w-full bg-transparent px-4 pb-3.5 pt-1.5 font-sans text-base text-white placeholder:text-white/25 focus:outline-none",
                className,
              )}
              {...props}
            />
            {isPassword && (
              <button
                type="button"
                data-testid={`${testId}-toggle`}
                aria-label={revealed ? "Hide password" : "Show password"}
                onClick={() => setRevealed((v) => !v)}
                className="mr-3 rounded-md p-1.5 text-cad transition-colors duration-200 hover:text-white"
              >
                {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            )}
          </div>
        </div>
        {error && (
          <motion.p
            data-testid={`${testId}-error`}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-1 font-mono text-[11px] uppercase tracking-[0.14em] text-red-400"
          >
            {error}
          </motion.p>
        )}
      </div>
    );
  },
);

AuthField.displayName = "AuthField";
